import {
  Flex,
  FormControl,
  FormLabel,
  Text,
  Select,
  Collapse,
  FormErrorMessage,
} from '@chakra-ui/react';
import AddonInput from '../AddonInput';
import BuilderSwitch from '../BuilderSwitch';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import { setFormFieldItemProp } from '../../../../store/redux/slices/form.slice';
import { clearError } from '../../../../store/redux/slices/templateErrors.slice';
import { FormFieldState, FormState } from '../../../../store/types/form';
import { useEffect } from 'react';
import FieldEditIndicator from '../FieldEditIndicator';
import { LabelWithTooltip } from '../LabelWithTooltip';
import { RefObject } from 'react';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import { getFormFieldError } from '../validation/validationUtils';

interface InputFormFieldRefs {
  idRef: RefObject<HTMLDivElement>;
  labelRef: RefObject<HTMLDivElement>;
  validationTypeRef: RefObject<HTMLDivElement>;
  requiredRef: RefObject<HTMLDivElement>;
  extIdRef: RefObject<HTMLDivElement>;
  validationPatternRef: RefObject<HTMLDivElement>;
  validationMessageRef: RefObject<HTMLDivElement>;
}

const InputFormField = ({
  fieldData,
  fieldIndex,
  templateFieldOptions,
  selectedByOtherFormFields,
  handleFocus,
  handleConnectToPassField,
  validateSection,
  form,
  markTouched,
  fieldRefs,
  activeFieldRef,
  isFieldBeingEdited,
}: {
  fieldData: FormFieldState;
  fieldIndex: number;
  templateFieldOptions: { id: string; label: string }[];
  selectedByOtherFormFields: Set<string>;
  handleFocus: (path: string) => void;
  handleConnectToPassField: (value: string) => void;
  validateSection: (context?: object, validationContext?: object) => void;
  form: FormState;
  markTouched: (field: 'id' | 'label' | 'pattern' | 'message') => void;
  fieldRefs: InputFormFieldRefs;
  activeFieldRef: RefObject<HTMLDivElement> | null;
  isFieldBeingEdited: boolean;
}) => {
  const dispatch = useAppDispatch();
  const errors = useAppSelector((s) => s.templateErrors);
  const getError = (path: string) => getFormFieldError(errors, path);
  const validation = fieldData.validation ?? {
    name: 'none',
    pattern: '',
    message: '',
  };
  const {
    idRef,
    labelRef,
    validationTypeRef,
    requiredRef,
    extIdRef,
    validationPatternRef,
    validationMessageRef,
  } = fieldRefs;

  useEffect(() => {
    if (fieldData.extId && !fieldData.required) {
      dispatch(
        setFormFieldItemProp({
          fieldId: fieldData._clientId ?? fieldData.id,
          key: 'required',
          value: true,
        })
      );
    }
  }, [fieldData.extId, fieldData.required, dispatch, fieldData.id]);

  return (
    <Flex direction="column" gap={6} mt={8}>
      <FieldEditIndicator
        fieldRef={idRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="115px"
      >
        <FormControl
          isInvalid={!!getError(`form.formFields.${fieldIndex}.id`)}
          isRequired={true}
        >
          <FormLabel variant="templateBuilderLabel">
            Connect to pass field
          </FormLabel>
          <Text fontSize="12px" my={4}>
            <em>
              Select a field from your pass to link this form input to. The
              connected value will be passed directly to your pass template.
            </em>
          </Text>
          <Select
            value={fieldData.id}
            onChange={(e) => handleConnectToPassField(e.target.value)}
            onFocus={() => {
              handleFocus(`form.formFields.${fieldIndex}.id`);
              markTouched('id');
            }}
            placeholder="Select a field..."
          >
            {templateFieldOptions.map((tf) => (
              <option
                key={tf.id}
                value={tf.id}
                disabled={
                  tf.id !== 'none' && selectedByOtherFormFields.has(tf.id)
                }
              >
                {tf.id === 'none' ? tf.label : tf.id}
              </option>
            ))}
          </Select>
          {getError(`form.formFields.${fieldIndex}.id`) && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {getError(`form.formFields.${fieldIndex}.id`)}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={labelRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="53px"
      >
        <FormControl
          isInvalid={!!getError(`form.formFields.${fieldIndex}.label`)}
        >
          <FormLabel variant="templateBuilderLabel">Heading</FormLabel>
          <AddonInput
            label="Label"
            placeholder="Enter a label"
            value={fieldData.label || ''}
            onChange={(value) => {
              markTouched('label');
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'label',
                  value: value as unknown as string,
                })
              );
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, label: value as string } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
            onFocus={() => {
              handleFocus(
                fieldData.id
                  ? `fields.${fieldData.id}.label`
                  : `form.formFields.${fieldIndex}.label`
              );
              markTouched('label');
            }}
            isRequired={!!fieldData.label}
          />
          {getError(`form.formFields.${fieldIndex}.label`) && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {getError(`form.formFields.${fieldIndex}.label`)}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={validationTypeRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="55px"
      >
        <FormControl>
          <FormLabel variant="templateBuilderLabel">Validation type</FormLabel>
          <Select
            value={validation.name}
            onFocus={() =>
              handleFocus(`form.formFields.${fieldIndex}.validation.name`)
            }
            onChange={(e) => {
              const newValidationName = e.target.value;
              const isSwitchingToCustom = newValidationName === 'custom';
              const nextValidation = isSwitchingToCustom
                ? {
                    ...validation,
                    name: newValidationName,
                    _patternTouched: false,
                    _messageTouched: false,
                  }
                : {
                    ...validation,
                    name: newValidationName,
                    pattern: '',
                    message: '',
                    _patternTouched: false,
                    _messageTouched: false,
                  };
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'validation',
                  value: nextValidation,
                })
              );
              if (newValidationName !== 'custom') {
                dispatch(
                  clearError(`form.formFields.${fieldIndex}.validation.pattern`)
                );
                dispatch(
                  clearError(`form.formFields.${fieldIndex}.validation.message`)
                );
              }
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex
                  ? {
                      ...f,
                      validation: nextValidation,
                    }
                  : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
            placeholder="Select a field..."
          >
            <option value="none">None</option>
            <option value="phone">Phone</option>
            <option value="email">Email</option>
            <option value="url">URL</option>
            <option value="custom">Custom</option>
          </Select>
        </FormControl>
      </FieldEditIndicator>
      <Collapse in={validation.name === 'custom'} animateOpacity>
        <Flex direction="column" gap={4}>
          <FieldEditIndicator
            fieldRef={validationPatternRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="60px"
          >
            <FormControl
              isInvalid={
                !!getError(`form.formFields.${fieldIndex}.validation.pattern`)
              }
            >
              <LabelWithTooltip
                label="Custom regex"
                tooltip="A custom validation rule that controls which values are accepted (advanced use only)."
              />
              <AddonInput
                label="Regex"
                placeholder="Enter a custom regex"
                value={validation.pattern || ''}
                onChange={(value) => {
                  markTouched('pattern');
                  dispatch(
                    setFormFieldItemProp({
                      fieldId: fieldData._clientId ?? fieldData.id,
                      key: 'validation',
                      value: {
                        ...validation,
                        pattern: value as string,
                        _patternTouched: true,
                      },
                    })
                  );
                  const updatedFormFields = form.formFields.map((f, i) =>
                    i === fieldIndex
                      ? {
                          ...f,
                          validation: {
                            ...(f.validation ?? {}),
                            pattern: value as string,
                            _patternTouched: true,
                          },
                        }
                      : f
                  );
                  validateSection({ ...form, formFields: updatedFormFields });
                }}
                onFocus={() => {
                  handleFocus(
                    fieldData.id
                      ? `fields.${fieldData.id}.validation.pattern`
                      : `form.formFields.${fieldIndex}.validation.pattern`
                  );
                  markTouched('pattern');
                }}
                isRequired={!!validation.pattern}
              />
              <FormErrorMessage
                pl={2}
                display="flex"
                alignItems="center"
                gap={1.5}
              >
                <CustomErrorIcon />
                {getError(`form.formFields.${fieldIndex}.validation.pattern`)}
              </FormErrorMessage>
            </FormControl>
          </FieldEditIndicator>
          <FieldEditIndicator
            fieldRef={validationMessageRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="53px"
          >
            <FormControl
              isInvalid={
                !!getError(`form.formFields.${fieldIndex}.validation.message`)
              }
            >
              <FormLabel variant="templateBuilderLabel">
                Custom error message
              </FormLabel>
              <AddonInput
                label="Message"
                placeholder="Enter a custom error message"
                value={validation.message || ''}
                onChange={(value) => {
                  markTouched('message');
                  dispatch(
                    setFormFieldItemProp({
                      fieldId: fieldData._clientId ?? fieldData.id,
                      key: 'validation',
                      value: {
                        ...validation,
                        message: value as string,
                        _messageTouched: true,
                      },
                    })
                  );
                  const updatedFormFields = form.formFields.map((f, i) =>
                    i === fieldIndex
                      ? {
                          ...f,
                          validation: {
                            ...(f.validation ?? {}),
                            message: value as string,
                            _messageTouched: true,
                          },
                        }
                      : f
                  );
                  validateSection({ ...form, formFields: updatedFormFields });
                }}
                onFocus={() => {
                  handleFocus(
                    fieldData.id
                      ? `fields.${fieldData.id}.validation.message`
                      : `form.formFields.${fieldIndex}.validation.message`
                  );
                  markTouched('message');
                }}
                isRequired={!!validation.message}
              />
              <FormErrorMessage
                pl={2}
                display="flex"
                alignItems="center"
                gap={1.5}
              >
                <CustomErrorIcon />
                {getError(`form.formFields.${fieldIndex}.validation.message`)}
              </FormErrorMessage>
            </FormControl>
          </FieldEditIndicator>
        </Flex>
      </Collapse>
      <FieldEditIndicator
        fieldRef={requiredRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="13px"
      >
        <Flex direction="row" width="100%" justifyContent="space-between">
          <Text>Require field?</Text>
          <BuilderSwitch
            isChecked={fieldData.required}
            onFocus={() =>
              handleFocus(`form.formFields.${fieldIndex}.required`)
            }
            onChange={(e) => {
              const checked = e.target.checked;
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'required',
                  value: checked,
                })
              );
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, required: checked } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
          />
        </Flex>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={extIdRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="12px"
      >
        <Flex direction="column" gap={2}>
          <Flex direction="row" width="100%" justifyContent="space-between">
            <LabelWithTooltip
              label="External ID"
              tooltip="A unique value used to identify a pass, coming from your own system (for example, a booking ID or user ID)."
            />
            <BuilderSwitch
              isChecked={fieldData.extId}
              onFocus={() => handleFocus(`form.formFields.${fieldIndex}.extId`)}
              onChange={(e) => {
                const checked = e.target.checked;
                dispatch(
                  setFormFieldItemProp({
                    fieldId: fieldData._clientId ?? fieldData.id,
                    key: 'extId',
                    value: checked,
                  })
                );
                const updatedFormFields = form.formFields.map((f, i) =>
                  i === fieldIndex ? { ...f, extId: checked } : f
                );
                validateSection({ ...form, formFields: updatedFormFields });
              }}
            />
          </Flex>
          <Text fontSize="12px">
            <em>
              Note: External ID can only be enabled if field is set to
              'required'.
            </em>
          </Text>
        </Flex>
      </FieldEditIndicator>
    </Flex>
  );
};

export default InputFormField;
